<template>
    <div class="pathological-section-modern">
        <div class="section-header-modern">
            <div class="header-title-modern">
                <i class="fas fa-notes-medical"></i>
                <h4>{{ apiData.title || 'Antecedentes Personales Patológicos' }}</h4>
            </div>
        </div>

        <!-- Botones para agregar campos -->
        <div v-if="inactiveFields.length > 0" class="add-fields-section">
            <button
v-for="field in inactiveFields" :key="field.name" class="add-field-btn"
                :title="'Agregar ' + field.label" @click="addField(field.name)">
                <i class="fas fa-plus"></i>
                <span>{{ field.label }}</span>
            </button>
        </div>

        <!-- Campos activos (con contenido o activados) -->
        <div v-if="activeFields.length > 0" class="fields-grid-compact">
            <div v-for="field in activeFields" :key="field.name" class="field-group-compact full-width">
                <div class="field-header">
                    <label class="form-label-modern">
                        <span class="label-content-modern">
                            <i class="fas fa-file-medical"></i>
                            <span>{{ field.label }}</span>
                        </span>
                    </label>
                    <button
v-if="isFieldCloseable(field.name)" class="close-field-btn" title="Cerrar campo"
                        @click="removeField(field.name)">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <div class="textarea-wrapper-modern">
                    <textarea
:value="field.model" class="form-textarea-modern"
                        :placeholder="getPlaceholder(field.name)" rows="3"
                        @input="updateFieldValue(field.name, $event.target.value)"></textarea>
                    <div class="textarea-footer-modern">
                        <div class="textarea-stats-modern">
                            <span class="stat-item-modern">
                                <i class="fas fa-font"></i>
                                {{ (field.model || '').length }} caracteres
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="last-updated-wrapper">
            <LastUpdated :updated-at="updated_at" />
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import api from '@/services/api';
import { saveToLocalStorage } from '@/store/Expedientes/pacientes';
import LastUpdated from "@/components/LastUpdated.vue";
import debounce from 'lodash.debounce';

const props = defineProps({
    patientRecord: {
        type: Object,
        required: true
    },
    data: {
        type: Object,
        default: () => ({})
    }
});

const apiData = {
    title: "Antecedentes Personales Patológicos",
    data: [
        { name: "eruptive", label: "Eruptivos", isVisible: true, category: "1" },
        { name: "traumatic", label: "Traumáticos", isVisible: true, category: "1" },
        { name: "transfusional", label: "Transfusionales", isVisible: true, category: "1" },
        { name: "surgical", label: "Quirúrgicos", isVisible: true, category: "1" },
        { name: "infectious", label: "Infecciosos", isVisible: true, category: "1" },
        { name: "tumoral", label: "Tumorales", isVisible: true, category: "1" },
        { name: "allergic", label: "Alérgicos", isVisible: true, category: "1" },
        { name: "diseases", label: "Enfermedades Crónicas", isVisible: true, category: "1" }
    ]
};

const defaultFields = [
    { name: 'eruptive', label: 'Eruptivos', isVisible: true, category: '1' },
    { name: 'traumatic', label: 'Traumáticos', isVisible: true, category: '1' },
    { name: 'transfusional', label: 'Transfusionales', isVisible: true, category: '1' },
    { name: 'surgical', label: 'Quirúrgicos', isVisible: true, category: '1' },
    { name: 'infectious', label: 'Infecciosos', isVisible: true, category: '1' },
    { name: 'tumoral', label: 'Tumorales', isVisible: true, category: '1' },
    { name: 'allergic', label: 'Alérgicos', isVisible: true, category: '1' },
    { name: 'diseases', label: 'Enfermedades Crónicas', isVisible: true, category: '1' }
];

const fields = ref((apiData.data && apiData.data.length > 0 ? apiData.data : defaultFields).map(field => ({
    ...field,
    model: '',
    isActive: false // Campo para controlar si está visible
})));

const showOpen = ref(true);
const updated_at = ref('');

// Computed para campos activos
const activeFields = computed(() => {
    return fields.value.filter(field => field.isActive);
});

// Computed para campos inactivos
const inactiveFields = computed(() => {
    return fields.value.filter(field => !field.isActive && field.isVisible === showOpen.value);
});

// Función para agregar campo
const addField = (name) => {
    const field = fields.value.find(f => f.name === name);
    if (field) {
        field.isActive = true;
    }
};

// Función para remover campo
const removeField = (name) => {
    const field = fields.value.find(f => f.name === name);
    if (field) {
        field.isActive = false;
    }
};

const isFieldCloseable = () => {
    return true;
};

const updateFieldValue = (name, value) => {
    const field = fields.value.find(field => field.name === name);
    if (field) {
        field.model = value;
        debouncedSavePathologicalData();
    }
};

// Placeholders personalizados
const getPlaceholder = (name) => {
    const placeholders = {
        eruptive: 'Ej: Dermatitis, rash, varicela, sarampión...',
        traumatic: 'Ej: Fracturas, accidentes, traumatismos craneoencefálicos...',
        transfusional: 'Ej: 2 paquetes globulares en 2020, plaquetas en 2021...',
        surgical: 'Ej: Apendicectomía (2018), cesárea (2020)...',
        infectious: 'Ej: Salmonelosis, chikungunya, COVID-19, tifoidea...',
        tumoral: 'Ej: Miomas uterinos, quistes de mama, pólipos...',
        allergic: 'Ej: Penicilina, lidocaína, ketorolaco, mariscos...',
        diseases: 'Ej: Diabetes mellitus tipo 2, hipertensión arterial, hipotiroidismo...'
    };
    return placeholders[name] || 'Describe los antecedentes...';
};


const savePathologicalData = async () => {
    const pathologicalData = {
        pathological: {}
    };

    fields.value.forEach(field => {
        pathologicalData.pathological[field.name] = field.model || '';
    });

    try {
        const response = await api.put(`/pacientes/${props.patientRecord.id}/pathological`, pathologicalData);
        const updatedAt = new Date(response.data.data.pathological.updated_at);
        updated_at.value = updatedAt.toString();
        saveToLocalStorage(`patient_${props.patientRecord.id}_pathological`, pathologicalData.pathological);
    } catch (error) {
        console.error('Error al guardar los datos patológicos:', error);
    }
};

// Debounce la función savePathologicalData para que se ejecute solo después de que el usuario deje de escribir
const debouncedSavePathologicalData = debounce(savePathologicalData, 1000);

watch(() => props.data, (newData) => {
    if (newData) {
        fields.value.forEach(field => {
            const incoming = newData[field.name];
            field.model = typeof incoming === 'string' ? incoming : (incoming ?? '');
            // Solo activar campos que tienen contenido válido
            field.isActive = field.model !== '' && field.model !== null;
        });

        if (newData.updated_at) {
            updated_at.value = new Date(newData.updated_at).toString();
        }
    }
}, { immediate: true });

</script>

<style scoped>
.pathological-section-modern {
    background: #ffffff;
    border-radius: 16px;
    padding: 1.5rem;
}

/* Section Header */
.section-header-modern {
    margin-bottom: 0.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #f0f2f5;
}

.header-title-modern {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.header-title-modern i {
    font-size: 1.5rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.header-title-modern h4 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    color: #344767;
}

/* Fields Grid */
.fields-grid-modern {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: 1rem;
}

.field-group-modern {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
}

.form-label-modern {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9375rem;
    font-weight: 600;
    color: #344767;
    margin-bottom: 0.5rem;
}

.label-content-modern {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.label-content-modern i {
    font-size: 0.9375rem;
    color: #667eea;
}

/* Textarea Wrapper */
.textarea-wrapper-modern {
    position: relative;
    background: white;
    border: 2px solid #e9ecef;
    border-radius: 12px;
    transition: all 0.3s ease;
}

.textarea-wrapper-modern:focus-within {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-textarea-modern {
    width: 100%;
    padding: 0.875rem 1rem;
    border: none;
    border-radius: 12px;
    font-size: 0.9375rem;
    font-weight: 400;
    color: #344767;
    line-height: 1.6;
    resize: vertical;
    min-height: 80px;
}

.form-textarea-modern:focus {
    outline: none;
}

.form-textarea-modern::placeholder {
    color: #adb5bd;
}

/* Textarea Footer */
.textarea-footer-modern {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 1rem;
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    border-top: 1px solid #e9ecef;
    border-radius: 0 0 12px 12px;
}

.textarea-stats-modern {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.stat-item-modern {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    color: #8392ab;
    font-weight: 500;
}

.stat-item-modern i {
    font-size: 0.75rem;
}

/* Help Text */
.help-text-modern {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    background: linear-gradient(135deg, #e7f3ff 0%, #f0f7ff 100%);
    border-left: 3px solid #5a9fd4;
    border-radius: 6px;
    font-size: 0.8125rem;
    color: #4a5568;
}

.help-text-modern i {
    font-size: 0.875rem;
    color: #5a9fd4;
}

/* Last Updated */
.last-updated-wrapper {
    margin-top: 0.5rem;
}

/* Campo Header Compact */
.field-header-compact {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
}

.close-field-btn {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #6b7280;
}

.close-field-btn:hover {
    background: #ff6b6b;
    border-color: #ff6b6b;
    color: white;
    transform: scale(1.05);
}

/* Sección de campos inactivos */
.inactive-fields-section {
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 2px dashed #e9ecef;
}

.inactive-fields-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1rem;
    font-weight: 600;
    color: #6b7280;
    margin-bottom: 1rem;
}

.inactive-fields-title i {
    color: #667eea;
}

.inactive-fields-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 0.75rem;
}

.add-field-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: white;
    border: 2px dashed #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s ease;
}

.add-field-btn:hover {
    background: linear-gradient(135deg, #667eea15 0%, #764ba215 100%);
    border-color: #667eea;
    color: #667eea;
    transform: translateY(-2px);
}

.add-field-btn i {
    font-size: 0.875rem;
}

/* Estilos compactos para gestión de campos */
.add-fields-section {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
    padding: 0.75rem;
    background: #f8f9fa;
    border-radius: 8px;
}

.add-fields-section .add-field-btn {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.75rem;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    color: #667eea;
    font-size: 0.813rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.add-fields-section .add-field-btn i {
    font-size: 0.75rem;
}

.add-fields-section .add-field-btn:hover {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-color: #667eea;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.fields-grid-compact {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
}

.field-group-compact {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border: 1px solid #e9ecef;
    border-radius: 12px;
    padding: 1rem;
    transition: all 0.3s ease;
}

.field-group-compact:hover {
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
    transform: translateY(-2px);
}

.field-group-compact.full-width {
    grid-column: 1 / -1;
}

.field-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #e9ecef;
}

.close-field-btn {
    padding: 0.25rem 0.5rem;
    background: transparent;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    color: #6b7280;
    font-size: 0.75rem;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
}

.close-field-btn:hover {
    background: #fee;
    border-color: #f87171;
    color: #dc2626;
}

/* Responsive */
@media (max-width: 768px) {
    .pathological-section-modern {
        padding: 1rem;
    }

    .fields-grid-modern {
        grid-template-columns: 1fr;
    }

    .inactive-fields-grid {
        grid-template-columns: 1fr;
    }
}
</style>
